import { useState } from 'react'
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Typography,
  App as AntdApp,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CommentOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface Ticket {
  id: number
  ticket_no: string
  subject: string
  status: string
  priority: string
  last_reply_at: string | null
  last_reply_by: string | null
  created_at: string
  department_id: number
}

interface Department {
  id: number
  name: string
}

export default function PortalTickets() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)

  const ticketsQ = useQuery({
    queryKey: ['portal-tickets'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: Ticket[] } }>(
        '/api/v1/portal/tickets',
      )
      return res.data.data.items
    },
  })

  const deptQ = useQuery({
    queryKey: ['portal-ticket-departments'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Department[] }>(
        '/api/v1/portal/tickets/departments',
      )
      return res.data.data
    },
  })

  const ticketStatusTag = (status: string) => {
    const colors: Record<string, string> = {
      open: 'blue', pending: 'orange', resolved: 'green', closed: 'default',
    }
    const labels: Record<string, string> = {
      open: t('portalTickets.statusOpen', 'Offen'),
      pending: t('portalTickets.statusPending', 'Wartend'),
      resolved: t('portalTickets.statusResolved', 'Gelöst'),
      closed: t('portalTickets.statusClosed', 'Geschlossen'),
    }
    return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>
  }

  const fmtDateTime = (iso: string | null) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const columns: ColumnsType<Ticket> = [
    {
      title: t('portal.tickets.columns.no', 'Ticket-Nr.'),
      dataIndex: 'ticket_no',
      key: 'ticket_no',
      width: 130,
      render: (v: string, r: Ticket) => (
        <Link to={`/portal/tickets/${r.id}`}>
          <Typography.Text code>{v}</Typography.Text>
        </Link>
      ),
    },
    {
      title: t('portal.tickets.columns.subject', 'Betreff'),
      dataIndex: 'subject',
      key: 'subject',
      render: (v: string, r: Ticket) => (
        <Link to={`/portal/tickets/${r.id}`} className="font-medium">{v}</Link>
      ),
    },
    {
      title: t('portal.tickets.columns.status', 'Status'),
      key: 'status',
      width: 120,
      render: (_: unknown, r) => ticketStatusTag(r.status),
    },
    {
      title: t('portal.tickets.columns.lastReply', 'Letzte Antwort'),
      dataIndex: 'last_reply_at',
      key: 'last_reply_at',
      width: 160,
      render: (v: string | null) => (
        <Typography.Text type="secondary" className="text-sm">
          {fmtDateTime(v)}
        </Typography.Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'right' as const,
      render: (_: unknown, r) => (
        <Link to={`/portal/tickets/${r.id}`}>
          <Button size="small">{t('common.view', 'Öffnen')}</Button>
        </Link>
      ),
    },
  ]

  const renderMobileCard = (r: Ticket) => (
    <div className="mob-card">
      <div className="flex-between items-start mb-8">
        <Link to={`/portal/tickets/${r.id}`} className="font-semibold text-14 flex-1 mr-8">
          {r.subject}
        </Link>
        {ticketStatusTag(r.status)}
      </div>
      <div className="flex-row flex-wrap gap-16">
        <Typography.Text type="secondary" className="text-sm">
          {r.ticket_no}
        </Typography.Text>
        {r.last_reply_at && (
          <Typography.Text type="secondary" className="text-sm">
            {t('portalTickets.lastReplyLabel', 'Letzte Antwort')}: {fmtDateTime(r.last_reply_at)}
          </Typography.Text>
        )}
      </div>
    </div>
  )

  return (
    <>
      <PageHeader
        icon={<CommentOutlined />}
        title={t('portal.tickets.title', 'Tickets')}
        subtitle={t('portal.tickets.subtitle', 'Ihre Support-Anfragen und offene Tickets.')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('portal.tickets.create', 'Neues Ticket')}
          </Button>
        }
      />
      {(() => {
        const tickets = ticketsQ.data || []
        const open = tickets.filter(r => r.status === 'open')
        const pending = tickets.filter(r => r.status === 'pending')
        const resolved = tickets.filter(r => r.status === 'resolved' || r.status === 'closed')
        return (
          <div className="kpi-grid kpi-grid-4 ticket-status-strip ticket-list-kpis mb-16">
            <div className="kpi-tile">
              <div className="kpi-tile-label"><CommentOutlined /> {t('portal.tickets.kpi.total', 'Tickets')}</div>
              <div className="kpi-tile-value">{tickets.length}</div>
              <div className="kpi-tile-sub">{t('portal.tickets.kpi.total_sub', 'gesamt')}</div>
            </div>
            <div className={`kpi-tile ${open.length > 0 ? 'kpi-info' : ''}`}>
              <div className="kpi-tile-label">{t('portal.tickets.kpi.open', 'Offen')}</div>
              <div className="kpi-tile-value kpi-info">{open.length}</div>
              <div className="kpi-tile-sub">{t('portal.tickets.kpi.open_sub', 'in Bearbeitung')}</div>
            </div>
            <div className={`kpi-tile ${pending.length > 0 ? 'kpi-warning' : ''}`}>
              <div className="kpi-tile-label">{t('portal.tickets.kpi.pending', 'Wartet')}</div>
              <div className="kpi-tile-value">{pending.length}</div>
              <div className="kpi-tile-sub">{t('portal.tickets.kpi.pending_sub', 'auf Antwort')}</div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile-label">{t('portal.tickets.kpi.resolved', 'Gelöst')}</div>
              <div className="kpi-tile-value">{resolved.length}</div>
              <div className="kpi-tile-sub">{t('portal.tickets.kpi.resolved_sub', 'geschlossen')}</div>
            </div>
          </div>
        )
      })()}
      <Card className="content-card">
        <DataTable<Ticket>
          rowKey="id"
          columns={columns}
          dataSource={ticketsQ.data || []}
          loading={ticketsQ.isLoading}
          searchPlaceholder={t(
            'portal.tickets.searchPlaceholder',
            'Suche Ticket-Nr. oder Betreff',
          )}
          renderMobileCard={renderMobileCard}
        />
      </Card>

      <PortalCreateTicket
        open={createOpen}
        departments={deptQ.data || []}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          message.success(t('portal.tickets.createSuccess', 'Ticket geöffnet'))
          setCreateOpen(false)
          navigate(`/portal/tickets/${id}`)
        }}
      />
    </>
  )
}

function PortalCreateTicket({
  open,
  departments,
  onClose,
  onCreated,
}: {
  open: boolean
  departments: Department[]
  onClose: () => void
  onCreated: (id: number) => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm()

  const submit = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const res = await apiClient.post<{ data: { id: number } }>('/api/v1/portal/tickets', values)
      return res.data.data
    },
    onSuccess: (ticket) => {
      form.resetFields()
      onCreated(ticket.id)
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.error', 'Ticket konnte nicht erstellt werden')),
  })

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('portal.tickets.create', 'Neues Ticket')}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submit.isPending }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => submit.mutate(v)}
        initialValues={{ priority: 'normal' }}
      >
        <Form.Item
          name="department_id"
          label={t('portal.tickets.fields.department', 'Abteilung')}
          rules={[{ required: true }]}
        >
          <Select
            options={departments.map((d) => ({ label: d.name, value: d.id }))}
            placeholder={t('portal.tickets.fields.departmentPlaceholder', 'Abteilung wählen')}
          />
        </Form.Item>
        <Form.Item
          name="subject"
          label={t('portal.tickets.fields.subject', 'Betreff')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="priority"
          label={t('portal.tickets.fields.priority', 'Priorität')}
        >
          <Select
            options={['low', 'normal', 'high', 'urgent'].map((p) => ({ label: p, value: p }))}
          />
        </Form.Item>
        <Form.Item
          name="body"
          label={t('portal.tickets.fields.body', 'Ihre Nachricht')}
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={6} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
